// 门店数据概况 — 复刻惠店 Android StoreDataSurveyFragment（com.ulucu.view.fragment）
// 8 个卡片（current + 环比） + 5 期 tabs（时/天/周/月/季）+ ECharts 趋势图 + 排行 + 设置
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Empty, message as antdMessage, Popover, Segmented, Spin } from 'antd';
import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	HolderOutlined,
	MinusCircleOutlined,
	PlusCircleOutlined,
	SettingOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import {
	DEFAULT_TYPES,
	fetchHomeConfig,
	fetchSeriesSummary,
	fetchStoreRanks,
	fetchTotalSummary,
	saveHomeConfig,
	type Period,
	type SeriesPoint,
	type StoreRankItem,
	type SummaryDataItem,
	type TotalSummaryItem,
	type TypeCode,
} from '../api/statistic.js';
import { formatDateRange, formatNumber, formatShortDate, marketCurrency } from '../utils/locale-format.js';

/** 统一时序点的 label：daily/today/week/month/quarter 字段不同 */
function pointLabel(p: SeriesPoint, period: Period, full = false): string {
	if (p.hour != null) return full ? `${p.hour}:00` : `${p.hour}`;
	if (p.date) return full ? p.date : p.date.slice(5);
	if (period === 'week' && p.year && p.week)        return full ? `${p.year}-W${p.week}` : `W${p.week}`;
	if (period === 'month' && p.year && p.month)      return full ? `${p.year}-${p.month}` : p.month;
	if (period === 'quarter' && p.year && p.quarter)  return full ? `${p.year}-Q${p.quarter}` : `Q${p.quarter}`;
	if (p.start_date) return full ? p.start_date : p.start_date.slice(5);
	return '';
}

const PALETTE = ['#FF7043', '#1890FF', '#52C41A', '#FAAD14', '#722ED1', '#13C2C2', '#EB2F96', '#666'];

const TYPE_LABEL_KEY: Record<string, string> = {
	'109001': 'sov.t.jzkl',
	'102002': 'sov.t.gdrs',
	'101006': 'sov.t.jdzhl',
	'101008': 'sov.t.xfds',
	'101009': 'sov.t.xsze',
	'101012': 'sov.t.cjl',
	'107001': 'sov.t.sjzs',
	'107002': 'sov.t.dclsjs',
	'101001': 'sov.t.zkl',
	'101005': 'sov.t.jtlsc',
	'101010': 'sov.t.kdj',
	'101011': 'sov.t.ldl',
	'101030': 'sov.t.gdrs_f',
	'111001': 'sov.t.gdrs',
	'111003': 'sov.t.jdzhl',
	'111004': 'sov.t.cjl',
};

/** 设置抽屉的候选目录（反编译 LanguageUtils.DEFAULT_STORE_PROFIT_ITEMS / HomePageUtils.DEFAULT_STORE_ITEMS） */
const CATALOG_GROUPS: Array<{ titleKey: string; types: string[] }> = [
	{ titleKey: 'sov.cat.business', types: ['101009', '101008', '101010', '101011', '101006', '101012'] },
	{ titleKey: 'sov.cat.traffic',  types: ['109001', '102002', '101030', '101001', '101005'] },
	{ titleKey: 'sov.cat.event',    types: ['107001', '107002'] },
];

function unitOfType(type: string, isPercent?: string, locale?: string): string {
	if (isPercent === '1') return '%';
	if (type === '101009') return marketCurrency(locale);
	if (type === '101008' || type === '104001') return '单';
	if (type === '107001' || type === '107002') return '件';
	if (type === '101005') return '分';
	return '人';
}

function fmtValue(value: string | undefined, isPercent?: string, locale?: string): string {
	if (!value || value === '0') return '0';
	const n = Number(value);
	if (Number.isNaN(n)) return value;
	// 后端 percent 字段是小数（0.0264），按 iOS 风格 ×100 显示为 2.64%
	if (isPercent === '1') return (n * 100).toFixed(2);
	return n % 1 === 0 ? formatNumber(n, locale) : n.toFixed(2);
}

function diffPercent(curr?: string, last?: string): { up: boolean; pct: string } | null {
	if (last == null) return null;
	const l = Number(last);
	if (!Number.isFinite(l) || l <= 0) return null;  // 基准为 0 或无效 → 无环比
	const c = Number(curr ?? 0);
	const pct = ((c - l) * 100) / l;
	return { up: pct >= 0, pct: Math.abs(pct).toFixed(2) };
}

function compareDateText(item: TotalSummaryItem | undefined): string {
	if (!item?.lastCycleStartDate) return '';
	const s = item.lastCycleStartDate;
	const e = item.lastCycleEndDate;
	if (!e || s === e) return s;
	return `${s} ~ ${e}`;
}

function currentDateText(item: TotalSummaryItem | undefined, fallback: string): string {
	if (!item?.startDate) return fallback;
	const s = item.startDate;
	const e = item.endDate;
	if (!e || s === e) return s;
	return `${s} ~ ${e}`;
}

const ymd = (d: Date) =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const today = () => ymd(new Date());

/** 周期 → 卡片日期范围（反编译 DateUtils.getFirstDayOfWeek / getFirstDayOMonth） */
function dateRangeOf(period: Period): { start: string; end: string } {
	const t0 = today();
	const d = new Date();
	switch (period) {
		case 'hour':
		case 'day':
			return { start: t0, end: t0 };
		case 'week': {
			const dayIdx = (d.getDay() + 6) % 7;  // Mon=0
			d.setDate(d.getDate() - dayIdx);
			return { start: ymd(d), end: t0 };
		}
		case 'month':
			d.setDate(1);
			return { start: ymd(d), end: t0 };
		case 'quarter': {
			const q = Math.floor(d.getMonth() / 3) * 3;
			d.setMonth(q, 1);
			return { start: ymd(d), end: t0 };
		}
	}
}

/** 环比期范围（反编译 StoreDataSurveyFragment.gerOldStartDate / getOldeEndDate）
 *  逻辑：本期长度 N 天 → 环比 = (start - N) ~ (start - 1)
 *  对周/月/季：环比 = 上一个完整周期 */
function lastCycleRangeOf(period: Period): { start: string; end: string } {
	const r = dateRangeOf(period);
	const startDate = new Date(r.start);
	if (period === 'hour' || period === 'day') {
		const d = new Date(startDate);
		d.setDate(d.getDate() - 1);
		return { start: ymd(d), end: ymd(d) };
	}
	if (period === 'week') {
		const d = new Date(startDate);
		d.setDate(d.getDate() - 7);
		const e = new Date(startDate);
		e.setDate(e.getDate() - 1);
		return { start: ymd(d), end: ymd(e) };
	}
	if (period === 'month') {
		const d = new Date(startDate);
		d.setMonth(d.getMonth() - 1);
		const e = new Date(startDate);
		e.setDate(e.getDate() - 1);
		return { start: ymd(d), end: ymd(e) };
	}
	// quarter
	const d = new Date(startDate);
	d.setMonth(d.getMonth() - 3);
	const e = new Date(startDate);
	e.setDate(e.getDate() - 1);
	return { start: ymd(d), end: ymd(e) };
}

/** 趋势图日期范围（更长，多点画线） */
function chartRangeOf(period: Period): { start: string; end: string } {
	const end = today();
	const d = new Date();
	if (period === 'hour')    return { start: end, end };
	if (period === 'day')     d.setDate(d.getDate() - 6);
	if (period === 'week')    d.setDate(d.getDate() - 28);
	if (period === 'month')   d.setMonth(d.getMonth() - 5);
	if (period === 'quarter') d.setMonth(d.getMonth() - 11);
	return { start: ymd(d), end };
}

interface Props {
	storeId: string;
}

export default function StoreOverview({ storeId }: Props) {
	const { t, i18n } = useTranslation();
	const [period, setPeriod] = useState<Period>('day');
	// types = null 表示尚未拿到配置；非 null 表示要么是后端返回，要么是 DEFAULT 兜底
	const [types, setTypes] = useState<TypeCode[] | null>(null);
	const [totals, setTotals] = useState<TotalSummaryItem[] | null>(null);
	const [series, setSeries] = useState<SummaryDataItem<SeriesPoint>[] | null>(null);
	const [ranks, setRanks] = useState<Record<string, StoreRankItem> | null>(null);

	// 隐藏哪些指标在趋势图中不画（不影响卡片）
	const [hiddenInChart, setHiddenInChart] = useState<Set<string>>(new Set());
	// 哪些指标显示均值线
	const [showAvgFor, setShowAvgFor] = useState<Set<string>>(new Set());

	// 设置抽屉
	const [settingsOpen, setSettingsOpen] = useState(false);

	// 1. 拿用户配置 — 拿到后才让卡片 / 趋势 / 排行 effects 跑（避免先用 DEFAULT 闪一下）
	useEffect(() => {
		fetchHomeConfig()
			.then((cfg) => setTypes(cfg && cfg.length > 0 ? cfg.slice(0, 8) : DEFAULT_TYPES))
			.catch(() => setTypes(DEFAULT_TYPES));
	}, []);

	// 2. 卡片：按周期算日期范围 + 并发拉环比期，client-side zip
	useEffect(() => {
		if (!storeId || !types) return;
		setTotals(null);
		const cur = dateRangeOf(period);
		const last = lastCycleRangeOf(period);
		Promise.all([
			fetchTotalSummary({ storeIds: storeId, startDate: cur.start, endDate: cur.end, types }),
			fetchTotalSummary({ storeIds: storeId, startDate: last.start, endDate: last.end, types }),
		]).then(([curr, lastArr]) => {
			const lastByType = new Map(lastArr.map((it) => [it.type, it.value]));
			const merged: TotalSummaryItem[] = curr.map((it) => ({
				...it,
				startDate: cur.start,
				endDate: cur.end,
				lastCycleStartDate: last.start,
				lastCycleEndDate: last.end,
				lastCycleValue: lastByType.get(it.type),
			}));
			setTotals(merged);
		}).catch(() => setTotals([]));
	}, [storeId, types, period]);

	// 3. 排行：同周期日期范围
	useEffect(() => {
		if (!storeId || !types) return;
		setRanks(null);
		const r = dateRangeOf(period);
		fetchStoreRanks({
			storeId,
			startDate: r.start,
			endDate: r.end,
			types,
		}).then(setRanks).catch(() => setRanks({}));
	}, [storeId, types, period]);

	// 4. 趋势图
	useEffect(() => {
		if (!storeId || !types) return;
		setSeries(null);
		const r = chartRangeOf(period);
		fetchSeriesSummary({
			storeIds: storeId,
			period,
			startDate: r.start,
			endDate: r.end,
			types,
		}).then(setSeries).catch(() => setSeries([]));
	}, [storeId, period, types]);

	const cards = useMemo(() => {
		if (!types) return [];
		const map = new Map<string, TotalSummaryItem>(
			(totals ?? []).map((it) => [it.type, it]),
		);
		return types.map((type) => map.get(type) ?? { type } as TotalSummaryItem);
	}, [totals, types]);

	const headerDateText = useMemo(() => {
		const r = dateRangeOf(period);
		const last = lastCycleRangeOf(period);
		const lang = i18n.language;
		const cur = r.start === r.end ? formatShortDate(r.start, lang) : formatDateRange(r.start, r.end, lang);
		const cmp = last.start === last.end ? formatShortDate(last.start, lang) : formatDateRange(last.start, last.end, lang);
		const showWeekday = (lang === 'zh-CN' || lang === 'zh-TW') && r.start === r.end;
		const weekday = showWeekday
			? ' ' + ['周日','周一','周二','周三','周四','周五','周六'][new Date(r.start).getDay()]
			: '';
		return `${cur}${weekday} (${t('sov.compare')} ${cmp})`;
	}, [i18n.language, t, period]);

	// 趋势图：按 hiddenInChart 过滤，按 showAvgFor 加平均线 markLine
	const chartOption = useMemo(() => {
		const items = (series ?? []).filter((it) => !hiddenInChart.has(it.type));
		if (items.length === 0) return null;

		const first = items[0];
		const xAxis = (first.items ?? []).map((p) => pointLabel(p, period));

		const lines = items.map((it) => {
			const colorIdx = (types as string[]).indexOf(it.type);
			const color = PALETTE[(colorIdx >= 0 ? colorIdx : 0) % PALETTE.length];
			const data = (it.items ?? []).map((p) => Number(p.value || 0));
			const showAvg = showAvgFor.has(it.type);
			const avg = data.length > 0
				? data.reduce((s, n) => s + n, 0) / data.length
				: 0;
			return {
				name: t(TYPE_LABEL_KEY[it.type] ?? '', it.type),
				type: 'line' as const,
				smooth: true,
				showSymbol: false,
				lineStyle: { width: 2 },
				itemStyle: { color },
				data,
				...(showAvg ? {
					markLine: {
						silent: true,
						symbol: 'none',
						lineStyle: { color, type: 'dashed' as const, width: 1.5 },
						label: { formatter: avg.toFixed(2), position: 'end' as const, color },
						data: [{ yAxis: avg, name: 'avg' }],
					},
				} : {}),
			};
		});

		return {
			grid: { left: 8, right: 8, top: 8, bottom: 24, containLabel: true },
			tooltip: { trigger: 'axis' as const },
			xAxis: {
				type: 'category' as const,
				data: xAxis,
				axisLine: { lineStyle: { color: '#ddd' } },
				axisLabel: { color: '#999', fontSize: 11 },
				boundaryGap: false,
			},
			yAxis: {
				type: 'value' as const,
				show: false,
				splitLine: { lineStyle: { color: '#f0f0f0' } },
			},
			series: lines,
		};
	}, [series, t, period, hiddenInChart, showAvgFor, types]);

	const chartLabels = useMemo(() => {
		const items = series ?? [];
		if (items.length === 0) return { start: '', end: '' };
		const arr = items[0].items ?? [];
		if (arr.length === 0) return { start: '', end: '' };
		return {
			start: pointLabel(arr[0], period, true),
			end:   pointLabel(arr[arr.length - 1], period, true),
		};
	}, [series, period]);

	const toggleHidden = (type: string) => {
		setHiddenInChart((prev) => {
			const next = new Set(prev);
			if (next.has(type)) next.delete(type);
			else next.add(type);
			return next;
		});
	};
	const toggleAvg = (type: string) => {
		setShowAvgFor((prev) => {
			const next = new Set(prev);
			if (next.has(type)) next.delete(type);
			else next.add(type);
			return next;
		});
	};

	if (types === null || totals === null) {
		return (
			<div style={{ padding: '40px 0', textAlign: 'center' }}>
				<Spin />
			</div>
		);
	}

	const eyeMenu = (
		<div style={{ minWidth: 240, padding: 4 }}>
			{cards.map((card) => {
				const labelKey = TYPE_LABEL_KEY[card.type];
				const label = labelKey ? t(labelKey) : card.type;
				const hidden = hiddenInChart.has(card.type);
				const avg = showAvgFor.has(card.type);
				return (
					<div key={card.type} style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '8px 4px',
						borderBottom: '1px solid #f5f5f5',
					}}>
						<div
							onClick={() => toggleHidden(card.type)}
							style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}
						>
							{hidden
								? <EyeInvisibleOutlined style={{ color: '#bbb', fontSize: 16 }} />
								: <EyeOutlined style={{ color: '#FF7043', fontSize: 16 }} />}
							<span style={{ fontSize: 13, color: hidden ? '#bbb' : '#222' }}>{label}</span>
						</div>
						<div
							onClick={() => toggleAvg(card.type)}
							style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', padding: '0 8px' }}
						>
							<span style={{
								width: 14, height: 14, borderRadius: '50%',
								border: avg ? '4px solid #FF7043' : '1px solid #d9d9d9',
								boxSizing: 'border-box',
							}} />
							<span style={{ fontSize: 12, color: '#666' }}>{t('sov.avg')}</span>
						</div>
					</div>
				);
			})}
		</div>
	);

	return (
		<div style={{ background: '#fff' }}>
			{/* Top toolbar — gear button */}
			<div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px 0' }}>
				<Button
					type="text"
					size="small"
					icon={<SettingOutlined style={{ fontSize: 18, color: '#FF7043' }} />}
					onClick={() => setSettingsOpen(true)}
				/>
			</div>

			{/* Header date */}
			<div style={{ padding: '4px 14px 8px', fontSize: 14, fontWeight: 600 }}>
				{headerDateText}
			</div>

			{/* 8 cards 2x4 */}
			<div style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(4, 1fr)',
				gap: 1,
				background: '#f0f0f0',
				margin: '0 8px',
			}}>
				{cards.map((card, idx) => {
					const labelKey = TYPE_LABEL_KEY[card.type];
					const label = labelKey ? t(labelKey) : card.type;
					const dot = PALETTE[idx % PALETTE.length];
					const diff = diffPercent(card.value, card.lastCycleValue);
					const unit = unitOfType(card.type, card.is_percent, i18n.language);
					return (
						<div key={card.type + idx} style={{ background: '#fff', padding: '10px 8px', minHeight: 78 }}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
								<span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, flex: '0 0 auto' }} />
								<span style={{ fontSize: 11, color: '#888', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{label}
								</span>
							</div>
							<div style={{ fontSize: 17, fontWeight: 700, color: '#222', lineHeight: 1.1 }}>
								{fmtValue(card.value, card.is_percent, i18n.language)}
								<span style={{ fontSize: 11, color: '#888', fontWeight: 400, marginInlineStart: 2 }}>
									{unit}
								</span>
							</div>
							<div style={{ fontSize: 11, marginTop: 3, height: 14 }}>
								{diff ? (
									<span style={{ color: diff.up ? '#FF4D4F' : '#52C41A' }}>
										{diff.up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
										{' '}{diff.pct}%
									</span>
								) : (
									<span style={{ color: '#bbb' }}>--</span>
								)}
							</div>
						</div>
					);
				})}
			</div>

			{/* Period tabs + eye popover */}
			<div style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				padding: '14px 14px 6px',
			}}>
				<Segmented
					value={period}
					onChange={(v) => setPeriod(v as Period)}
					options={[
						{ label: t('sov.p.hour'),    value: 'hour'    },
						{ label: t('sov.p.day'),     value: 'day'     },
						{ label: t('sov.p.week'),    value: 'week'    },
						{ label: t('sov.p.month'),   value: 'month'   },
						{ label: t('sov.p.quarter'), value: 'quarter' },
					]}
					style={{ background: 'transparent' }}
				/>
				<Popover
					trigger="click"
					placement="bottomRight"
					content={eyeMenu}
					styles={{ body: { padding: 6 } }}
				>
					<EyeOutlined style={{ fontSize: 18, color: '#FF7043', cursor: 'pointer', padding: 4 }} />
				</Popover>
			</div>

			{/* Chart */}
			<div style={{ height: 230, padding: '0 8px' }}>
				{series === null ? (
					<div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<Spin />
					</div>
				) : chartOption ? (
					<ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'svg' }} />
				) : (
					<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('common.no_data')} style={{ marginTop: 50 }} />
				)}
			</div>

			{series && series.length > 0 && (
				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
					padding: '4px 18px 12px',
					fontSize: 11,
					color: '#888',
				}}>
					<span>{chartLabels.start}</span>
					<span>{chartLabels.end}</span>
				</div>
			)}

			{/* ===== 该门店数据排行 ===== */}
			<div style={{ borderTop: '8px solid #f5f5f5', padding: '14px 14px 6px' }}>
				<div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
					{t('sov.rank_title')}
				</div>
				{ranks === null ? (
					<div style={{ padding: '20px 0', textAlign: 'center' }}>
						<Spin />
					</div>
				) : (
					<div>
						{cards.map((card, idx) => {
							const r = ranks[card.type];
							const labelKey = TYPE_LABEL_KEY[card.type];
							const label = labelKey ? t(labelKey) : card.type;
							const dot = PALETTE[idx % PALETTE.length];
							const unit = unitOfType(card.type, card.is_percent, i18n.language);
							const value = fmtValue(r?.value ?? card.value, card.is_percent, i18n.language);
							const exceedPct = r?.exceed
								? Math.round(Number(r.exceed) * 100).toString()
								: null;
							const bAvg = r?.b_avg ? fmtValue(r.b_avg, card.is_percent, i18n.language) : null;
							const top = r?.top ? Number(r.top) : null;
							return (
								<div key={card.type + idx} style={{ padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
									<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
										<span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flex: '0 0 auto' }} />
										<span style={{ fontSize: 13, color: '#888' }}>{label}</span>
										<span style={{ fontSize: 18, fontWeight: 700, color: '#222', marginInlineStart: 4 }}>
											{value}
										</span>
										<span style={{ fontSize: 11, color: '#888', marginInlineStart: 1 }}>{unit}</span>
										{top != null && top > 0 && (
											<span style={{ marginInlineStart: 6, fontSize: 16 }}>
												<RankMedal rank={top} />
											</span>
										)}
									</div>
									<div style={{ fontSize: 12, color: '#999', marginTop: 4, marginInlineStart: 13 }}>
										{r ? (
											<>
												{t('sov.b_avg')} {bAvg}{unit}
												{exceedPct && (
													<>，{t('sov.exceed', { pct: exceedPct })}</>
												)}
											</>
										) : '--'}
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>

			<div style={{ height: 24 }} />

			{/* ===== 设置 Drawer ===== */}
			<SettingsDrawer
				open={settingsOpen}
				onClose={() => setSettingsOpen(false)}
				selectedTypes={types ?? DEFAULT_TYPES}
				onSave={(next) => {
					setTypes(next as TypeCode[]);
					setSettingsOpen(false);
				}}
			/>
		</div>
	);
}

/** 1=金 / 2=银 / 3=铜 / 其他=数字徽章 */
function RankMedal({ rank }: { rank: number }) {
	if (rank === 1) return <span title="rank 1">🥇</span>;
	if (rank === 2) return <span title="rank 2">🥈</span>;
	if (rank === 3) return <span title="rank 3">🥉</span>;
	return (
		<span style={{
			display: 'inline-block',
			minWidth: 18,
			height: 18,
			lineHeight: '18px',
			padding: '0 5px',
			borderRadius: 9,
			background: '#bfbfbf',
			color: '#fff',
			fontSize: 11,
			textAlign: 'center',
			fontWeight: 700,
			verticalAlign: 'middle',
		}}>{rank}</span>
	);
}

/** 设置抽屉：选/取消 8 项 + 拖拽排序 + 保存 */
function SettingsDrawer({
	open,
	onClose,
	selectedTypes,
	onSave,
}: {
	open: boolean;
	onClose: () => void;
	selectedTypes: string[];
	onSave: (next: string[]) => void;
}) {
	const { t } = useTranslation();
	const [draft, setDraft] = useState<string[]>(selectedTypes);
	const [saving, setSaving] = useState(false);
	const [dragIdx, setDragIdx] = useState<number | null>(null);

	useEffect(() => {
		if (open) setDraft(selectedTypes);
	}, [open, selectedTypes]);

	const remove = (type: string) => setDraft((prev) => prev.filter((x) => x !== type));
	const add = (type: string) => {
		if (draft.includes(type) || draft.length >= 8) return;
		setDraft((prev) => [...prev, type]);
	};

	const onDrop = (toIdx: number) => {
		if (dragIdx === null || dragIdx === toIdx) return;
		setDraft((prev) => {
			const next = [...prev];
			const [moved] = next.splice(dragIdx, 1);
			next.splice(toIdx, 0, moved);
			return next;
		});
		setDragIdx(null);
	};

	const submit = async () => {
		setSaving(true);
		const r = await saveHomeConfig(draft);
		setSaving(false);
		if (r.ok) {
			antdMessage.success(t('sov.saved'));
			onSave(draft);
		} else {
			antdMessage.error(`${t('sov.save_failed')}: ${r.msg ?? ''}`);
		}
	};

	const draftSet = new Set(draft);

	return (
		<Drawer
			placement="bottom"
			height="92%"
			open={open}
			onClose={onClose}
			title={t('sov.settings_title')}
			extra={
				<Button type="primary" size="small" loading={saving} onClick={submit} style={{ background: '#FF7043', borderColor: '#FF7043' }}>
					{t('sov.done')}
				</Button>
			}
			styles={{ body: { padding: 0, background: '#fafafa' } }}
		>
			<div style={{ padding: '12px 12px 6px', fontWeight: 600 }}>
				{t('sov.selected_data')}
				<span style={{ fontSize: 11, color: '#888', fontWeight: 400, marginInlineStart: 6 }}>
					({t('sov.selected_hint')})
				</span>
			</div>

			<div style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(2, 1fr)',
				gap: 8,
				padding: '0 12px 16px',
			}}>
				{draft.map((type, idx) => {
					const labelKey = TYPE_LABEL_KEY[type];
					const label = labelKey ? t(labelKey) : type;
					return (
						<div
							key={type}
							draggable
							onDragStart={() => setDragIdx(idx)}
							onDragOver={(e) => e.preventDefault()}
							onDrop={() => onDrop(idx)}
							style={{
								background: '#fff',
								border: '1px solid #f0f0f0',
								borderRadius: 6,
								padding: '10px 26px 10px 10px',
								position: 'relative',
								cursor: 'move',
							}}
						>
							<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
								<HolderOutlined style={{ color: '#bbb', fontSize: 12 }} />
								<span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
							</div>
							<MinusCircleOutlined
								onClick={() => remove(type)}
								style={{
									position: 'absolute',
									top: -8,
									insetInlineEnd: -8,
									fontSize: 18,
									color: '#FF4D4F',
									background: '#fff',
									borderRadius: '50%',
									cursor: 'pointer',
								}}
							/>
						</div>
					);
				})}
			</div>

			{CATALOG_GROUPS.map((g) => {
				const candidates = g.types.filter((tp) => !draftSet.has(tp));
				if (candidates.length === 0) return null;
				return (
					<div key={g.titleKey} style={{ marginBottom: 6 }}>
						<div style={{ padding: '8px 12px', color: '#999', fontSize: 12, background: '#f0f0f0' }}>
							{t(g.titleKey)}
						</div>
						{candidates.map((tp) => {
							const labelKey = TYPE_LABEL_KEY[tp];
							const label = labelKey ? t(labelKey) : tp;
							const disabled = draft.length >= 8;
							return (
								<div
									key={tp}
									onClick={() => !disabled && add(tp)}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 12,
										padding: '14px 14px',
										background: '#fff',
										borderBottom: '1px solid #f5f5f5',
										cursor: disabled ? 'not-allowed' : 'pointer',
										opacity: disabled ? 0.5 : 1,
									}}
								>
									<PlusCircleOutlined style={{ fontSize: 22, color: disabled ? '#ddd' : '#52C41A' }} />
									<span style={{ fontSize: 14 }}>{label}</span>
								</div>
							);
						})}
					</div>
				);
			})}
		</Drawer>
	);
}
